import { UNSET_OR_NOTAPPLICABLE_VALUE_LABEL } from '../../consts';
import {
  APIDashboardFilterExpression,
  APIDashboardFilterSuggestion,
  SupportedDashboardFilterType,
} from '@ardoq/api-types';
import type {
  HierarchicalListCheckboxState,
  DashboardFilterPillsUIStateWithSetters,
  DashboardFilterPillValueProps,
  DatePopoverProps,
  HierarchicalListPopoverOptionProps,
  HierarchicalListPopoverProps,
  ListPopoverOptionProps,
  ListPopoverProps,
  NumberPopoverProps,
} from '@ardoq/table-filter-bar';
import { IconName } from '@ardoq/icons';
import { debounce, partition } from 'lodash';
import type {
  DashboardFilter,
  DashboardFilterSuggestions,
  HierarchicalDashboardFilterSuggestion,
} from '../../types';
import { toBreadCrumb } from '../../utils';
import type { Locale } from '@ardoq/locale';
import { type I18nLocale, t } from '@ardoq/i18n';

type HierarchicalListOptionsGroups = {
  rootsOptions: HierarchicalListPopoverOptionProps[];
  checkedOptions: HierarchicalListPopoverOptionProps[];
};

const getSelectableSelfAndDescendantValues = (
  suggestion: HierarchicalDashboardFilterSuggestion
): string[] => {
  const self = suggestion.isSelectable ? [suggestion.value] : [];
  return [
    ...self,
    ...suggestion.children.flatMap(getSelectableSelfAndDescendantValues),
  ];
};

const computeHierarchicalListCheckboxState = (
  values: string[],
  selectedValues: Set<string>
): HierarchicalListCheckboxState => {
  const selectedCount = values.filter(v => selectedValues.has(v)).length;
  if (selectedCount === 0) return 'unchecked';
  if (selectedCount === values.length) return 'checked';
  return 'mixed';
};

const toOptionProps = (
  suggestion: HierarchicalDashboardFilterSuggestion,
  selectedValues: Set<string>,
  onToggle: (value: string) => void,
  onToggleMany: (values: string[]) => void
): HierarchicalListPopoverOptionProps => {
  if (suggestion.children.length > 0) {
    const mappedChildren = suggestion.children.map(child =>
      toOptionProps(child, selectedValues, onToggle, onToggleMany)
    );
    const descendantValues = getSelectableSelfAndDescendantValues(suggestion);
    const checkboxState = computeHierarchicalListCheckboxState(
      descendantValues,
      selectedValues
    );
    const handleToggleAll = () => onToggleMany(descendantValues);

    // Prepend self as first child so the node's own value can be toggled individually
    const selfChild: HierarchicalListPopoverOptionProps | null =
      suggestion.isSelectable
        ? {
            id: `${suggestion.value}_self`, // ensure a unique id that won't conflict with itself when rendered as both a parent and a child
            label: suggestion.label,
            parentId: suggestion.value,
            isReadOnly: false,
            checkboxState: selectedValues.has(suggestion.value)
              ? 'checked'
              : 'unchecked',
            onClick: () => onToggle(suggestion.value),
            onCheckboxClick: () => onToggle(suggestion.value),
            hideCheckbox: false,
            isDisabled: false,
            icon: null,
            children: [],
          }
        : null;

    const children = selfChild
      ? [selfChild, ...mappedChildren]
      : mappedChildren;

    return {
      id: suggestion.value,
      label: suggestion.label,
      parentId: suggestion.parentValue,
      isReadOnly: false,
      checkboxState,
      onClick: handleToggleAll,
      onCheckboxClick: handleToggleAll,
      hideCheckbox: false,
      isDisabled: false,
      icon: null,
      children,
    };
  }

  if (suggestion.isSelectable) {
    const handleSelect = () => onToggle(suggestion.value);
    return {
      id: suggestion.value,
      label: suggestion.label,
      parentId: suggestion.parentValue,
      isReadOnly: false,
      checkboxState: selectedValues.has(suggestion.value)
        ? 'checked'
        : 'unchecked',
      onClick: handleSelect,
      onCheckboxClick: handleSelect,
      hideCheckbox: false,
      isDisabled: false,
      icon: null,
      children: [],
    };
  }

  return {
    id: suggestion.value,
    label: suggestion.label,
    parentId: suggestion.parentValue,
    isReadOnly: true,
    icon: null,
    children: [],
  };
};

const toHierarchicalListOptionsGroups = (
  hierarchicalSuggestions: HierarchicalDashboardFilterSuggestion[],
  selectedOptions: {
    value: string;
    label: string;
  }[],
  onToggle: (value: string) => void,
  onToggleMany: (values: string[]) => void
): HierarchicalListOptionsGroups => {
  const selectedValues = new Set(selectedOptions.map(option => option.value));

  // Group root suggestions by workspace, creating workspace nodes
  const workspaceMap = new Map<
    string,
    {
      workspaceOption: HierarchicalListPopoverOptionProps;
      children: HierarchicalListPopoverOptionProps[];
    }
  >();

  for (const suggestion of hierarchicalSuggestions) {
    const ws = suggestion.workspace;
    if (!workspaceMap.has(ws.id)) {
      workspaceMap.set(ws.id, {
        workspaceOption: {
          id: ws.id,
          label: ws.name,
          parentId: null,
          isReadOnly: true,
          icon: { name: IconName.WORKSPACE },
          children: [],
        },
        children: [],
      });
    }
    workspaceMap
      .get(ws.id)!
      .children.push(
        toOptionProps(suggestion, selectedValues, onToggle, onToggleMany)
      );
  }

  const rootsOptions: HierarchicalListPopoverOptionProps[] = Array.from(
    workspaceMap.values()
  ).map(({ workspaceOption, children }) => ({ ...workspaceOption, children }));

  const checkedOptions: HierarchicalListPopoverOptionProps[] =
    selectedOptions.map(option => ({
      id: option.value,
      label: option.label,
      parentId: null,
      isReadOnly: false,
      checkboxState: 'checked',
      onClick: () => onToggle(option.value),
      onCheckboxClick: () => onToggle(option.value),
      hideCheckbox: false,
      isDisabled: false,
      icon: null,
      children: [],
    }));

  return { rootsOptions, checkedOptions };
};

export const toListPopoverOption = (
  { value, label, secondaryInformation }: APIDashboardFilterSuggestion,
  values: string[],
  onValueChange: (values: string[]) => void,
  hideCheckbox?: boolean
): ListPopoverOptionProps => {
  const isChecked = values.includes(value);
  return {
    id: value,
    icon: null,
    rightIcon: null,
    label: label,
    count: null,
    isChecked,
    hideCheckbox,
    breadCrumbs: secondaryInformation?.map(toBreadCrumb),
    onClick: () =>
      onValueChange(
        isChecked
          ? values.filter(selectedValue => selectedValue !== value)
          : [...values, value]
      ),
    onCheckboxClick: () =>
      onValueChange(
        isChecked
          ? values.filter(selectedValue => selectedValue !== value)
          : [...values, value]
      ),
  };
};

type GetValueOptionsParams = {
  options: APIDashboardFilterSuggestion[];
  values: string[];
  onValueChange: (values: string[]) => void;
  dashboardFilterSuggestionsProps?: DashboardFilterSuggestions;
  requestDashboardFilterSuggestions: (query: string) => void;
  includeFreeTextOption: boolean;
  i18nLocale: I18nLocale;
  filterInputPlaceholder?: string;
};

const suggestionFooterText = (
  i18nLocale: I18nLocale,
  widgetFilteredReports?: string[]
) => {
  if (!widgetFilteredReports || widgetFilteredReports.length === 0) {
    return undefined;
  }
  const reports = widgetFilteredReports.join(', ');
  return widgetFilteredReports.length === 1
    ? t(
        i18nLocale,
        'dashboard:filter.pill.widgetFilteredReportsSingular',
        { reports },
        'Chart filters on the report "{{reports}}" may be limiting the suggestions.'
      )
    : t(
        i18nLocale,
        'dashboard:filter.pill.widgetFilteredReportsPlural',
        { reports },
        'Chart filters on the reports "{{reports}}" may be limiting the suggestions.'
      );
};

export const getListPopoverPropsWithFilter = ({
  includeFreeTextOption,
  i18nLocale,
  filterInputPlaceholder,
  ...param
}: GetValueOptionsParams): ListPopoverProps => {
  const footer = suggestionFooterText(
    i18nLocale,
    param.dashboardFilterSuggestionsProps?.widgetFilteredReports
  );

  const [defaultOptions, suggestions] = partition(
    param.options,
    option => option.value === UNSET_OR_NOTAPPLICABLE_VALUE_LABEL
  );
  const query = param.dashboardFilterSuggestionsProps?.query;
  return {
    type: 'list',
    groups: [
      ...(includeFreeTextOption && query
        ? [
            {
              label: '',
              options: [
                toListPopoverOption(
                  {
                    value: query,
                    label: t(
                      i18nLocale,
                      'dashboard:filter.pill.searchByQuery',
                      { query },
                      `Search by "{{query}}"`
                    ),
                  },
                  param.values,
                  param.onValueChange,
                  includeFreeTextOption
                ),
              ],
            },
          ]
        : []),
      {
        label: t(
          i18nLocale,
          'dashboard:filter.popoverGroup.defaultOptions',
          'default options'
        ),
        options: defaultOptions.map(option =>
          toListPopoverOption(option, param.values, param.onValueChange)
        ),
      },
      {
        label: t(
          i18nLocale,
          'dashboard:filter.popoverGroup.suggestions',
          'suggestions'
        ),
        options: suggestions
          .filter(({ value }) => value) // FIXME: hack to set the operator without having selected a value first. We should make it possible change the operator independently
          .map(option =>
            toListPopoverOption(option, param.values, param.onValueChange)
          ),
      },
    ],
    filter: {
      isLoading: param.dashboardFilterSuggestionsProps?.isLoading,
      value: query ?? '',
      onChange: debounce(param.requestDashboardFilterSuggestions, 100, {
        leading: true,
        trailing: false,
      }),
      placeholder: filterInputPlaceholder,
    },
    footer,
  };
};

type GetHierarchicalValueOptionsParams = {
  options: HierarchicalDashboardFilterSuggestion[];
  selectedOptions: {
    value: string;
    label: string;
  }[];
  onValueChange: (values: string[]) => void;
  dashboardFilterSuggestionsProps?: DashboardFilterSuggestions;
  requestDashboardFilterSuggestions: (query: string) => void;
  filterCachedHierarchicalSuggestions: (query: string) => void;
  i18nLocale: I18nLocale;
  filterInputPlaceholder?: string;
};

const getUnsetOrNotApplicableOption = (
  checkboxState: HierarchicalListCheckboxState,
  onToggle: (value: string) => void
): HierarchicalListPopoverOptionProps => ({
  id: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
  label: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
  parentId: null,
  isReadOnly: false,
  checkboxState,
  hideCheckbox: false,
  isDisabled: false,
  onClick: () => onToggle(UNSET_OR_NOTAPPLICABLE_VALUE_LABEL),
  onCheckboxClick: () => onToggle(UNSET_OR_NOTAPPLICABLE_VALUE_LABEL),
  icon: null,
  children: [],
});

export const getHierarchicalListPopoverPropsWithFilter = (
  param: GetHierarchicalValueOptionsParams
): HierarchicalListPopoverProps => {
  const selectedValuesList = param.selectedOptions.map(option => option.value);
  const selectedValues = new Set(selectedValuesList);
  const query = param.dashboardFilterSuggestionsProps?.query;

  const onToggle = (value: string) => {
    if (selectedValues.has(value)) {
      param.onValueChange(selectedValuesList.filter(v => v !== value));
    } else {
      param.onValueChange([...selectedValuesList, value]);
    }
  };

  const onToggleMany = (values: string[]) => {
    const allSelected = values.every(v => selectedValues.has(v));
    if (allSelected) {
      const valuesToRemove = new Set(values);
      param.onValueChange(
        selectedValuesList.filter(v => !valuesToRemove.has(v))
      );
    } else {
      const toAdd = values.filter(v => !selectedValues.has(v));
      param.onValueChange([...selectedValuesList, ...toAdd]);
    }
  };

  // only show the UNSET_OR_NOTAPPLICABLE_VALUE_LABEL when there is no query or the query matches it
  const defaultOptions =
    !query ||
    UNSET_OR_NOTAPPLICABLE_VALUE_LABEL.toLowerCase().includes(
      query.toLowerCase()
    )
      ? [
          getUnsetOrNotApplicableOption(
            selectedValues.has(UNSET_OR_NOTAPPLICABLE_VALUE_LABEL)
              ? 'checked'
              : 'unchecked',
            onToggle
          ),
        ]
      : [];

  const { rootsOptions, checkedOptions } = toHierarchicalListOptionsGroups(
    param.options,
    param.selectedOptions,
    onToggle,
    onToggleMany
  );

  return {
    type: 'hierarchicalList',
    groups: [
      {
        label: t(
          param.i18nLocale,
          'dashboard:filter.popoverGroup.defaultOptions',
          'default options'
        ),
        variant: 'flat',
        options: defaultOptions,
      },
      {
        label: t(
          param.i18nLocale,
          'dashboard:filter.popoverGroup.selectedOptions',
          'selected options'
        ),
        variant: 'flat',
        // Only show the flat selected options in the "selected options" group when there is no query
        // when there is a query, the selected options will be shown in the suggestions group below with the hierarchy preserved
        options: !query ? checkedOptions : [],
      },
      {
        label: t(
          param.i18nLocale,
          'dashboard:filter.popoverGroup.suggestions',
          'suggestions'
        ),
        variant: 'hierarchical',
        options: rootsOptions,
      },
    ],
    filter: {
      isLoading: param.dashboardFilterSuggestionsProps?.isLoading,
      value: query ?? '',
      onChange: debounce(param.filterCachedHierarchicalSuggestions, 100, {
        leading: true,
        trailing: false,
      }),
      placeholder: param.filterInputPlaceholder,
    },
    footer: suggestionFooterText(
      param.i18nLocale,
      param.dashboardFilterSuggestionsProps?.widgetFilteredReports
    ),
  };
};

export const getValueProps = ({
  name,
  labelsForSelectedValues,
  clearFilter,
  isInvalid,
  hasLinkedWidgets,
  hasInaccessibleWidgets,
  filterPillUIState,
  onClick,
  onClose,
  content,
  i18nLocale,
}: {
  labelsForSelectedValues: string[];
  name: string;
  isInvalid: boolean;
  hasLinkedWidgets: boolean;
  hasInaccessibleWidgets: boolean;
  clearFilter: VoidFunction;
  filterPillUIState: DashboardFilterPillsUIStateWithSetters;
  onClick: VoidFunction;
  onClose: VoidFunction;
  i18nLocale: I18nLocale;
  content:
    | ListPopoverProps
    | HierarchicalListPopoverProps
    | NumberPopoverProps
    | DatePopoverProps;
}): DashboardFilterPillValueProps => ({
  button: {
    label:
      labelsForSelectedValues.length === 0
        ? t(
            i18nLocale,
            'dashboard:filter.pill.selectValuePlaceholder',
            'select...'
          )
        : labelsForSelectedValues.length === 1
          ? `${labelsForSelectedValues[0]}`
          : t(
              i18nLocale,
              'dashboard:filter.pill.selectedCount',
              { count: labelsForSelectedValues.length },
              '{{count}} selected'
            ),
    isDisabled: isInvalid || !hasLinkedWidgets || hasInaccessibleWidgets,
    ...(labelsForSelectedValues.length > 0
      ? {
          showClearButton: true,
          clearFilter,
        }
      : { showClearButton: false }),
    isPopoverOpen: filterPillUIState.isValuePopoverOpen,
    onClick,
  },
  popover: {
    isOpen: filterPillUIState.isValuePopoverOpen,
    content,
    onClose,
  },
  filterLabel: name,
});

export type GetOperatorAndValuePropsParams = {
  filterPillUIState: DashboardFilterPillsUIStateWithSetters;
  dashboardFilter: DashboardFilter;
  updateDashboardFilterValues: (
    dashboardFilterId: string,
    type: SupportedDashboardFilterType,
    values: APIDashboardFilterExpression[] | null
  ) => void;
  requestDashboardFilterSuggestions: (
    dashboardFilterId: string,
    query: string
  ) => void;
  requestDashboardFilterHierarchicalSuggestions: (
    dashboardFilterId: string,
    query: string
  ) => void;
  filterCachedHierarchicalSuggestions: (
    dashboardFilterId: string,
    query: string
  ) => void;
  hasInaccessibleWidgets: boolean;
  isLoading: boolean;
  locale: Locale;
  i18nLocale: I18nLocale;
  filterInputPlaceholder?: string;
};
